<script setup>
import { useRoute } from "vue-router"
import { useCustomSwal } from "@/composables/usePopup"

import RecruiterService from "@/services/RecruiterService"
import FooterBlank from "@/layouts/components/FooterBlank.vue"

definePage({
  meta: {
    layout: "blank",
    public: true,
  },
})
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR

const { showCustomSwal } = useCustomSwal()
const route = useRoute()
const childRef = ref(null)

const info = ref({})
const loading = ref(false)
const errorData = ref(null)

const onSubmit = async (obj) => {
  try {
    loading.value = true
    const response = await RecruiterService.registerRecruiter({
      ...obj,
    })

    if (response) {
      await showCustomSwal({
        title: $gettext("Data successfully saved"),
        html: `
        <p class="mb-0" style="font-size: 16px; font-style: normal">${$gettext("An email containing a participation link has been sent to you."
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
        timer: 10000,
        timerProgressBar: true,
        footer: `
        <p class="mb-0" style="font-size: 14px">
          ${$gettext("If you do not receive it: check your spam folder.")}
          </p>
          <p class="mb-3" style="font-size: 14px">${$gettext("If you have nothing in your spam folder,"
          )}</p>
          <p class="mb-1" style="font-size: 14px"><strong>${$gettext("DO NOT REGISTER AGAIN"
          )}</strong> ${$gettext("and contact us by email at: ")}
            <a class="text-justify text-sky" href='mailto:mastersurvey@eduniversal-group.com' target="_blank">
              mastersurvey@eduniversal-group.com
            </a>
          </p>
          `,
      })
    }
  } catch (error) {
    errorData.value = error?.data
    if (errorData.value?.code === "LIMIT_RECRUITER_REGISTERED") {
      await showCustomSwal({
        html: `
            <p class=" text-danger fw-bold">
            ${$gettext("The maximum number of registrations for this program has been reached. If you believe this is an error, you can contact our support team at . ")}
            <a href="mailto:mastersurvey@eduniversal-group.com" class="fw-bold text-primary">
                  mastersurvey@eduniversal-group.com
              </a>
          </p>
            `,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    } else if (errorData.value?.message === "already_participated") {
      await showCustomSwal({
        title: $gettext("You are already registered to respond to the Eduniversal survey."
        ),
        html: `
          <p style="font-size: 16px">
            ${$gettext("We have just resent you the participation email (please remember to check your spam folder)."
            )}
          </p>
          <p style="font-size: 16px">
            ${$gettext("If you do not receive the email, please contact our support team at "
            )}
            <a class="text-justify text-primary" href='mailto:mastersurvey@eduniversal-group.com' target="_blank" >mastersurvey@eduniversal-group.com</a>
          </p>
      `,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonColor: "#d33",
        cancelButtonText: $gettext("CLOSE"),
      })
    } else if (errorData.value?.message === "already_completed") {
      await showCustomSwal({
        title: $gettext("You have already responded to the Eduniversal survey"),
        html: `
          <p style="font-size: 16px">
            ${$gettext("We have just resent you the confirmation email (please remember to check your spam folder)."
            )}
          </p>
          <p style="font-size: 16px">
            ${$gettext("If you do not receive the email, please contact our support team at "
            )}
            <a class="text-justify text-primary" href='mailto:mastersurvey@eduniversal-group.com' target="_blank" >mastersurvey@eduniversal-group.com</a>
          </p>
      `,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonColor: "#d33",
        cancelButtonText: $gettext("CLOSE"),
      })
    }
  } finally {
    childRef.value?.resetInput()
    loading.value = false
  }
}

onMounted(async () => {
  const { params } = route
  try {
    const res = await RecruiterService.getRegisterRecruiter(params.id)
    info.value = res || {}
  } catch (error) {
    errorData.value = error?.data
  }
})
</script>

<template>
  <div class="register-student">
    <div class="register-student__container" style="max-width: 900px">
      <div class="register-student__bg">
        <img src="@/assets/images/palm_bg.svg" alt="palm" />
      </div>
      <!-- Start Header -->
      <header
        class="d-flex align-center justify-space-between register-student__container-header"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img
            src="@/assets/logo-eduniversal.png"
            alt="Logo"
            style="max-width: 140px"
          />
        </RouterLink>
        <div></div>
        <!-- <BtnLang @onChange="onChangeLang" /> -->
      </header>
      <main
        style="background: #fff; padding: 0 40px"
        :class="[
          'register-student__main py-6',
          errorData?.code ? 'd-flex align-center flex-column' : '',
        ]"
      >
        <ContentError v-if="errorData?.code" :code="errorData?.code" />
        <template v-else>
          <div>
            <!-- <h1 v-if="info?.program?.type === 2">
              {{ $gettext("Enquêtes") }} Eduniversal {{ CURRENT_YEAR }}
              <div style="font-size: 1.85rem; color: #C99B6C">
                {{ $gettext("des") }} Meilleurs Licences, Bachelors
                {{ $gettext("and") }} Grandes Écoles
              </div>
            </h1> -->
            <h1>
            {{ $gettext("Eduniversal Programs Surveys") }} {{ CURRENT_YEAR }}
            <div style="font-size: 1.85rem; color: #C99B6C">
              of the Best Master’s, MS and MBA Programs
            </div>
          </h1>
          </div>

          <hr class="my-4" />

          <FormRegisterRecruiter
            :data="info"
            :loading="loading"
            @update:modelValue="onSubmit($event)"
            ref="childRef"
            class="mt-4"
          />
        </template>
      </main>
    </div>
    <VCard class="register-student__contact">
      <ContactWithUs />
    </VCard>
    <FooterBlank :footerRh="true" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";

.swal2-title {
  font-size: 16px;
  font-weight: 700;
}
</style>
